import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaEnvelope,
  FaCreditCard,

  FaShieldAlt,
  FaCog,
  FaSave,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaSync,
  FaBell,

} from 'react-icons/fa';
import './SystemSettings.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    email: {
      host: '',
      port: '',
      user: '',
      password: '',
      secure: true,
      enabled: false
    },
    payment: {
      cashfree: {
        appId: '',
        secretKey: '',
        environment: 'sandbox',
        enabled: false
      }
    },
    notifications: {
      orderConfirmation: true,
      statusUpdates: true,
      adminAlerts: true,
      workshopReminders: true
    },
    general: {
      siteName: 'Godai Studios',
      supportEmail: '',
      timezone: 'Asia/Kolkata',
      currency: 'INR',
      language: 'en'
    },
    security: {
      sessionTimeout: 30,
      passwordMinLength: 8,
      twoFactorAuth: false,
      allowGuestCheckout: true
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('email');
  const [showPasswords, setShowPasswords] = useState({});
  const [connectionStatus, setConnectionStatus] = useState({});
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
      
      // Test connections
      await testConnections();
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const testConnections = async () => {
    try {
      const [emailTest, paymentTest] = await Promise.allSettled([
        axios.get(`${API_URL}/api/admin/test/email`),
        axios.get(`${API_URL}/api/admin/test/payment`)
      ]);

      setConnectionStatus({
        email: emailTest.status === 'fulfilled' ? 'connected' : 'error',
        payment: paymentTest.status === 'fulfilled' ? 'connected' : 'error'
      });
    } catch (error) {
      console.error("Error testing connections:", error);
    }
  };

  const saveSettings = async (section) => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/admin/settings/${section}`, settings[section]);
      
      // Test connection after saving
      if (section === 'email' || section === 'payment') {
        await testConnections();
      }
      
      alert('Settings saved successfully!');
    } catch (error) {
      console.error("Error saving settings:", error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const testEmailConnection = async () => {
    try {
      setTestResults(prev => ({ ...prev, email: 'testing' }));
      const response = await axios.post(`${API_URL}/api/admin/test/email`, settings.email);
      setTestResults(prev => ({ ...prev, email: 'success' }));
      alert('Email test sent successfully! Check your inbox.');
    } catch (error) {
      setTestResults(prev => ({ ...prev, email: 'error' }));
      alert('Email test failed. Please check your configuration.');
    }
  };

  const testPaymentGateway = async () => {
    try {
      setTestResults(prev => ({ ...prev, payment: 'testing' }));
      const response = await axios.post(`${API_URL}/api/admin/test/payment`, settings.payment.cashfree);
      setTestResults(prev => ({ ...prev, payment: 'success' }));
      alert('Payment gateway connection successful!');
    } catch (error) {
      setTestResults(prev => ({ ...prev, payment: 'error' }));
      alert('Payment gateway test failed. Please check your credentials.');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <FaCheck className="status-icon success" />;
      case 'error':
        return <FaTimes className="status-icon error" />;
      case 'testing':
        return <FaSync className="status-icon testing spin" />;
      default:
        return <FaExclamationTriangle className="status-icon warning" />;
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const updateSetting = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateNestedSetting = (section, subsection, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const tabs = [
    { id: 'email', label: 'Email Configuration', icon: FaEnvelope },
    { id: 'payment', label: 'Payment Gateway', icon: FaCreditCard },
    { id: 'notifications', label: 'Notifications', icon: FaBell },
    { id: 'general', label: 'General Settings', icon: FaCog },
    { id: 'security', label: 'Security', icon: FaShieldAlt }
  ];

  if (loading) {
    return (
      <div className="system-settings">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading system settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="system-settings">
      <div className="settings-header">
        <h1>System Settings</h1>
        <p>Configure your system settings and integrations</p>
      </div>

      <div className="settings-container">
        {/* Sidebar Navigation */}
        <div className="settings-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon />
              <span>{tab.label}</span>
              {(tab.id === 'email' || tab.id === 'payment') && (
                <div className="connection-indicator">
                  {getStatusIcon(connectionStatus[tab.id])}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Settings Content */}
        <div className="settings-content">
          {/* Email Configuration */}
          {activeTab === 'email' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Email Configuration</h2>
                <div className="section-actions">
                  <button 
                    className="btn btn-outline" 
                    onClick={testEmailConnection}
                    disabled={testResults.email === 'testing'}
                  >
                    {testResults.email === 'testing' ? 'Testing...' : 'Test Connection'}
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => saveSettings('email')}
                    disabled={saving}
                  >
                    <FaSave /> {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>SMTP Host</label>
                  <input
                    type="text"
                    value={settings.email.host}
                    onChange={(e) => updateSetting('email', 'host', e.target.value)}
                    placeholder="smtp.hostinger.com"
                  />
                  <small>Your SMTP server hostname</small>
                </div>

                <div className="form-group">
                  <label>SMTP Port</label>
                  <input
                    type="number"
                    value={settings.email.port}
                    onChange={(e) => updateSetting('email', 'port', e.target.value)}
                    placeholder="587"
                  />
                  <small>Usually 587 for TLS or 465 for SSL</small>
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={settings.email.user}
                    onChange={(e) => updateSetting('email', 'user', e.target.value)}
                    placeholder="contact@aerodip.com"
                  />
                  <small>Your email address for sending emails</small>
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <div className="password-input">
                    <input
                      type={showPasswords.email ? "text" : "password"}
                      value={settings.email.password}
                      onChange={(e) => updateSetting('email', 'password', e.target.value)}
                      placeholder="Enter your email password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => togglePasswordVisibility('email')}
                    >
                      {showPasswords.email ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <small>Your email account password</small>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.email.secure}
                      onChange={(e) => updateSetting('email', 'secure', e.target.checked)}
                    />
                    <span>Use SSL/TLS</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.email.enabled}
                      onChange={(e) => updateSetting('email', 'enabled', e.target.checked)}
                    />
                    <span>Enable Email Service</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Payment Gateway */}
          {activeTab === 'payment' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Payment Gateway - Cashfree</h2>
                <div className="section-actions">
                  <button 
                    className="btn btn-outline" 
                    onClick={testPaymentGateway}
                    disabled={testResults.payment === 'testing'}
                  >
                    {testResults.payment === 'testing' ? 'Testing...' : 'Test Gateway'}
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => saveSettings('payment')}
                    disabled={saving}
                  >
                    <FaSave /> {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>App ID</label>
                  <input
                    type="text"
                    value={settings.payment.cashfree.appId}
                    onChange={(e) => updateNestedSetting('payment', 'cashfree', 'appId', e.target.value)}
                    placeholder="Your Cashfree App ID"
                  />
                </div>

                <div className="form-group">
                  <label>Secret Key</label>
                  <div className="password-input">
                    <input
                      type={showPasswords.payment ? "text" : "password"}
                      value={settings.payment.cashfree.secretKey}
                      onChange={(e) => updateNestedSetting('payment', 'cashfree', 'secretKey', e.target.value)}
                      placeholder="Your Cashfree Secret Key"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => togglePasswordVisibility('payment')}
                    >
                      {showPasswords.payment ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Environment</label>
                  <select
                    value={settings.payment.cashfree.environment}
                    onChange={(e) => updateNestedSetting('payment', 'cashfree', 'environment', e.target.value)}
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.payment.cashfree.enabled}
                      onChange={(e) => updateNestedSetting('payment', 'cashfree', 'enabled', e.target.checked)}
                    />
                    <span>Enable Cashfree Payment Gateway</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Notification Settings</h2>
                <div className="section-actions">
                  <button 
                    className="btn btn-primary" 
                    onClick={() => saveSettings('notifications')}
                    disabled={saving}
                  >
                    <FaSave /> {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>

              <div className="notification-settings">
                <div className="notification-group">
                  <h3>Customer Notifications</h3>
                  <div className="notification-items">
                    <label className="notification-item">
                      <input
                        type="checkbox"
                        checked={settings.notifications.orderConfirmation}
                        onChange={(e) => updateSetting('notifications', 'orderConfirmation', e.target.checked)}
                      />
                      <div className="notification-info">
                        <span className="notification-title">Order Confirmation</span>
                        <span className="notification-desc">Send email when order is placed</span>
                      </div>
                    </label>

                    <label className="notification-item">
                      <input
                        type="checkbox"
                        checked={settings.notifications.statusUpdates}
                        onChange={(e) => updateSetting('notifications', 'statusUpdates', e.target.checked)}
                      />
                      <div className="notification-info">
                        <span className="notification-title">Status Updates</span>
                        <span className="notification-desc">Send email when order status changes</span>
                      </div>
                    </label>

                    <label className="notification-item">
                      <input
                        type="checkbox"
                        checked={settings.notifications.workshopReminders}
                        onChange={(e) => updateSetting('notifications', 'workshopReminders', e.target.checked)}
                      />
                      <div className="notification-info">
                        <span className="notification-title">Workshop Reminders</span>
                        <span className="notification-desc">Send reminders before workshop dates</span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="notification-group">
                  <h3>Admin Notifications</h3>
                  <div className="notification-items">
                    <label className="notification-item">
                      <input
                        type="checkbox"
                        checked={settings.notifications.adminAlerts}
                        onChange={(e) => updateSetting('notifications', 'adminAlerts', e.target.checked)}
                      />
                      <div className="notification-info">
                        <span className="notification-title">Admin Alerts</span>
                        <span className="notification-desc">Get notified of new orders and important events</span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>General Settings</h2>
                <div className="section-actions">
                  <button 
                    className="btn btn-primary" 
                    onClick={() => saveSettings('general')}
                    disabled={saving}
                  >
                    <FaSave /> {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Site Name</label>
                  <input
                    type="text"
                    value={settings.general.siteName}
                    onChange={(e) => updateSetting('general', 'siteName', e.target.value)}
                    placeholder="Godai Studios"
                  />
                </div>

                <div className="form-group">
                  <label>Support Email</label>
                  <input
                    type="email"
                    value={settings.general.supportEmail}
                    onChange={(e) => updateSetting('general', 'supportEmail', e.target.value)}
                    placeholder="support@godaistudios.com"
                  />
                </div>

                <div className="form-group">
                  <label>Timezone</label>
                  <select
                    value={settings.general.timezone}
                    onChange={(e) => updateSetting('general', 'timezone', e.target.value)}
                  >
                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Currency</label>
                  <select
                    value={settings.general.currency}
                    onChange={(e) => updateSetting('general', 'currency', e.target.value)}
                  >
                    <option value="INR">Indian Rupee (₹)</option>
                    <option value="USD">US Dollar ($)</option>
                    <option value="EUR">Euro (€)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Language</label>
                  <select
                    value={settings.general.language}
                    onChange={(e) => updateSetting('general', 'language', e.target.value)}
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Security Settings</h2>
                <div className="section-actions">
                  <button 
                    className="btn btn-primary" 
                    onClick={() => saveSettings('security')}
                    disabled={saving}
                  >
                    <FaSave /> {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Session Timeout (minutes)</label>
                  <input
                    type="number"
                    value={settings.security.sessionTimeout}
                    onChange={(e) => updateSetting('security', 'sessionTimeout', parseInt(e.target.value))}
                    min="5"
                    max="1440"
                  />
                  <small>How long users stay logged in without activity</small>
                </div>

                <div className="form-group">
                  <label>Minimum Password Length</label>
                  <input
                    type="number"
                    value={settings.security.passwordMinLength}
                    onChange={(e) => updateSetting('security', 'passwordMinLength', parseInt(e.target.value))}
                    min="6"
                    max="20"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.security.twoFactorAuth}
                      onChange={(e) => updateSetting('security', 'twoFactorAuth', e.target.checked)}
                    />
                    <span>Enable Two-Factor Authentication</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.security.allowGuestCheckout}
                      onChange={(e) => updateSetting('security', 'allowGuestCheckout', e.target.checked)}
                    />
                    <span>Allow Guest Checkout</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings; 